import { LoaderOverlay } from "@audacia-hq/shared/components";
import { faBell, faLightbulb } from "@fortawesome/free-regular-svg-icons";
import {
  faCheck, faCircleExclamation,
  faCircleNotch,
  faCreditCard, faHourglass, faMessage, faPen,
  faQuestionCircle,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover, Transition } from "@headlessui/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { usePathname } from "next/navigation";
import * as React from "react";
import {
  Fragment,
  useEffect, useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthContext";
import { useExpert } from "../../context/ExpertContext";
import { useMember } from "../../context/MemberContext";
import { WSMessage, useWS } from "../../context/WSContext";
import { ACK_ALL_NOTIFICATIONS, CREATE_NOTIFICATION_ACK } from "../../graphql/mutations";
import { GET_NOTIFICATIONS } from "../../graphql/query";
import { useLazyFetcher } from "../../hooks/useClientFetcher";
import { Notification } from "../../models/notification";

import DefaultNotif from "./DefaultNotif";
import LinkNotif from "./LinkNotif";
import QueueNotif from "./QueueNotif";

const Notifications: React.FC = () => {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const { expertIdentifiers } = useExpert();
  const panelRef = useRef<HTMLDivElement>(null);
  const [loadingNotificationUids, setLoadingNotificationUids] = useState<string[]>([]);

  const { notifications: initialNotifications } = useMember();
  const [notifications, setNotifications] = useState<{ data: Notification[], count: number }>({
    data: initialNotifications?.notifications || [], count: initialNotifications?.unreadNotificationsCount || 0
  });
  const [getNotificationsData, { loading }] = useLazyFetcher<{
    notification: { notifications: Notification[]; unreadNotificationsCount: number; };
  }
  >(GET_NOTIFICATIONS, { authenticated: true });

  const [ackAllNotifications, { loading: loadingAckAll }] = useLazyFetcher(ACK_ALL_NOTIFICATIONS, { authenticated: true });
  const [createNotificationAck] = useLazyFetcher(CREATE_NOTIFICATION_ACK, { authenticated: true });
  const ws = useWS();

  const handleGetNotifications = () => getNotificationsData({ variables: { offset: 0, limit: 10 } })
    .then((res) => {
      setNotifications({ data: res.data.notification.notifications, count: res.data.notification.unreadNotificationsCount });
    }).catch(() => null);

  useEffect(() => {
    if (!user) return;
    if (!initialNotifications) handleGetNotifications();
  }, [user]);

  useEffect(() => {
    const sub = ws.subscribe((msg) => handleNotificationEvents(msg as WSMessage));
    return () => sub.unsubscribe();
  }, [ws]);

  useEffect(() => {
    if (!isOpen) return;
    setIsOpen(false);
  }, [pathname]);

  const handleClickOutside = (event) => {
    if (panelRef.current && !panelRef.current.contains(event.target)) {
      setIsOpen(false);
      event.stopPropagation();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleNotificationEvents = (msg: WSMessage) => {
    switch (msg.event) {
      case "ws.reconnected":
        handleGetNotifications();
        break;
      case "member.notification.sent":
        handleGetNotifications();
        displayToast(msg);
        break;
      default:
        break;
    }
  };

  const displayToast = (msg: WSMessage) => {
    const notification = JSON.parse(msg.payload);
    const parsed = JSON.parse(notification.metadata);
    const metadata = parsed?.expertUid
      ? { expertPseudo: expertIdentifiers[parsed.expertUid]?.pseudo || t("anExpert"), ...parsed }
      : parsed;
    toast.info(<>{t(`notifications.${notification.notificationType}.body`, metadata)}</>);
  };

  const handleCreateNotificationAck = async (notificationUid: string) => {
    if (loadingAckAll) return;
    setLoadingNotificationUids((prev) => [...prev, notificationUid]);
    await createNotificationAck({
      variables: {
        notificationUid,
      },
    });
    handleGetNotifications();
  };

  const acknowledgeAll = async () => {
    if (notifications?.data?.some((n) => loadingNotificationUids.includes(n.notificationUid))) return;
    setLoadingNotificationUids(() => notifications?.data?.map((n) => n.notificationUid));
    await ackAllNotifications().then(handleGetNotifications);
  };

  const renderNotif = (notification: Notification) => {
    if (notification.notificationType.startsWith("MEMBER_QUEUE_ITEM_")) {
      return <QueueNotif notification={notification} icon={<FontAwesomeIcon icon={faHourglass} />} />;
    }
    const metadata = JSON.parse(notification.metadata);
    switch (notification.notificationType) {
      case "MEMBER_WRITTEN_CANCELED":
      case "MEMBER_WRITTEN_ANSWERED":
        return <LinkNotif notification={notification} link="/my-consultations" icon={<FontAwesomeIcon icon={faPen} />} />;
      case "MEMBER_RATING_REFUSED":
      case "MEMBER_NEW_RATING":
        return <LinkNotif notification={notification} link="/my-consultations" icon={<FontAwesomeIcon icon={faStar} />} />;
      case "MEMBER_NEW_PRIVATE_MESSAGE":
        return <LinkNotif notification={notification} link="/messages" icon={<FontAwesomeIcon icon={faMessage} />} />;
      case "ORDER_SUCCESS":
        return <LinkNotif notification={notification} link="/my-purchases" icon={<FontAwesomeIcon icon={faCreditCard} />} />;
      case "PRIVATE_MESSAGE_REFUSED":
        return <LinkNotif notification={notification} link={`/messages?correspondentUid=${metadata.recipient}`} icon={<FontAwesomeIcon icon={faMessage} />} />;
      case "SUPPORT_CREATE_TICKET":
        return metadata.link
          ? <LinkNotif notification={notification} link={metadata.link} icon={<FontAwesomeIcon icon={faQuestionCircle} />} />
          : <DefaultNotif notification={notification} />;
      case "SUPPORT_TICKET_ANSWERED":
        return metadata.message
          ? <LinkNotif notification={notification} link={metadata.message} icon={<FontAwesomeIcon icon={faQuestionCircle} />} />
          : <DefaultNotif notification={notification} />;
      default:
        return <DefaultNotif notification={notification} />;
    }
  };

  const notificationTime = (dateString: string): string => {
    const date = dayjs(dateString);
    if (date.date() === dayjs().date()) return date.format("LT");
    return date.format("L LT");
  };

  return (
    <div className="w-8 mr-2 md:mr-6 relative">
      <Popover>
        {({ open }) => (
          <>
            <Popover.Button data-pw="notificationsIcon" className="relative" onClick={() => setIsOpen(!isOpen)}>
              <FontAwesomeIcon icon={faBell} className="hover fa-lg" />
              {notifications?.count > 0 && (
                <div data-pw="unreadNotificationsCount" className="absolute -right-[8px] -top-[8px] bg-red-500 rounded-lg w-[20px] h-[20px] text-white text-xs flex items-center justify-center">
                  {notifications.count}
                </div>
              )}
            </Popover.Button>
            <Transition
              show={isOpen}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="fixed left-0 sm:left-auto sm:absolute sm:-right-5 z-50 mt-3 w-screen sm:w-[400px] transform px-4" ref={panelRef}>
                <div className="rounded-lg shadow-lg ring-1 ring-black/5">
                  <div className="flex justify-between items-baseline bg-white p-4 border-b border-gray">
                    {t("notifications.title")}
                    {notifications?.data?.length > 0 && (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                      <span
                        data-pw="notificationsClearAll"
                        className="text-xs font-semibold text-blue-600 cursor-pointer"
                        onClick={() => acknowledgeAll()}
                      >
                        {t("notifications.clearAll")}
                      </span>
                    )}
                  </div>
                  <div className="bg-white overflow-y-auto h-[300px] rounded-b-lg">
                    <LoaderOverlay show={loading} />
                    {notifications?.data?.length === 0 && (
                      <div className="flex justify-center items-center h-full">
                        <div className="flex-row space-y-3 text-center">
                          <div style={{ fontSize: 38 }}><FontAwesomeIcon icon={faLightbulb} /></div>
                          <div>{t("notifications.empty")}</div>
                        </div>
                      </div>
                    )}
                    {notifications?.data?.map((notification, index) => (
                      <div
                        key={notification.notificationUid}
                        className={clsx(
                          "px-5 py-4",
                          (notifications?.data?.length || 0) - 1 !== index && "border-b border-gray",
                          !notification.ack && "bg-slate-100",
                        )}
                      >
                        <div
                          className="flex gap-4 items-center justify-between"
                        >
                          {notification.priority && (
                            <FontAwesomeIcon
                              icon={faCircleExclamation}
                              className="fa-lg mt-1 text-blue-500"
                            />
                          )}
                          {renderNotif(notification)}
                          {!notification.ack && (
                            <button
                              type="button"
                              className="p-1 hover:text-blue-500 transition-all duration-150"
                              onClick={() => handleCreateNotificationAck(notification.notificationUid)}
                            >
                              {loadingAckAll || !loadingNotificationUids.includes(notification.notificationUid)
                                ? <FontAwesomeIcon icon={faCheck} />
                                : <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />}
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-right">{notificationTime(notification.createdAt)}</div>
                      </div>

                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default Notifications;
